/**
 * One durable-persistence timeout for every upload lane.
 *
 * Both the recording chunk stream and the small-file lane used to carry their own copy of this
 * helper. The copies differed in exactly one way that mattered: only one of them rejected a
 * non-finite or non-positive budget up front instead of handing it to `setTimeout`, which coerces
 * `NaN` to `0` and silently turns a misconfigured timeout into an immediate rejection on the next
 * macrotask. The guard variant is the shared one.
 */
export class PersistTimeoutError extends Error {
	constructor(timeoutMs: number, label = "Durable upload persistence") {
		super(`${label} timed out after ${timeoutMs}ms`);
		this.name = "PersistTimeoutError";
	}
}

export function withPersistTimeout<T>(operation: Promise<T>, timeoutMs: number, label?: string): Promise<T> {
	if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return Promise.reject(new PersistTimeoutError(timeoutMs, label));
	let timer: ReturnType<typeof setTimeout> | undefined;
	return new Promise<T>((resolve, reject) => {
		timer = setTimeout(() => reject(new PersistTimeoutError(timeoutMs, label)), timeoutMs);
		operation.then(resolve, reject).finally(() => {
			if (timer !== undefined) clearTimeout(timer);
		});
	});
}
